import { useState } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import {
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  Plus,
  Globe,
  User,
  LogOut,
  Loader2,
  Eye,
  Upload,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bg: string; icon: React.ElementType }
> = {
  pending: { label: "Хүлээгдэж байна", color: "text-amber-400", bg: "bg-amber-400/10 border-amber-400/20", icon: Clock },
  reviewing: { label: "Шалгаж байна", color: "text-blue-400", bg: "bg-blue-400/10 border-blue-400/20", icon: Eye },
  docs_needed: { label: "Баримт бичиг шаардлагатай", color: "text-orange-400", bg: "bg-orange-400/10 border-orange-400/20", icon: AlertCircle },
  processing: { label: "Боловсруулж байна", color: "text-purple-400", bg: "bg-purple-400/10 border-purple-400/20", icon: Loader2 },
  submitted: { label: "Илгээгдсэн", color: "text-cyan-400", bg: "bg-cyan-400/10 border-cyan-400/20", icon: Globe },
  approved: { label: "Зөвшөөрөгдсөн", color: "text-green-400", bg: "bg-green-400/10 border-green-400/20", icon: CheckCircle2 },
  rejected: { label: "Татгалзсан", color: "text-red-400", bg: "bg-red-400/10 border-red-400/20", icon: XCircle },
  completed: { label: "Дууссан", color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/20", icon: CheckCircle2 },
};

function ApplicationCard({ app }: { app: any }) {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;

  return (
    <motion.div
      layout
      className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
    >
      <div
        className="p-5 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">{app.country}</h3>
              <p className="text-gray-400 text-sm">{app.visaType}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${status.bg} ${status.color}`}>
              <StatusIcon className="w-3 h-3" />
              {status.label}
            </span>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
          <span>#{app.id}</span>
          <span>•</span>
          <span>{new Date(app.createdAt).toLocaleDateString("mn-MN")}</span>
          {app.serviceFee && (
            <>
              <span>•</span>
              <span className="text-blue-400">{app.serviceFee}</span>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-white/10 pt-4">
              {/* Personal Info */}
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div>
                  <span className="text-gray-500">Нэр:</span>
                  <span className="text-white ml-2">{app.lastName} {app.firstName}</span>
                </div>
                <div>
                  <span className="text-gray-500">Паспорт:</span>
                  <span className="text-white ml-2">{app.passportNumber}</span>
                </div>
                <div>
                  <span className="text-gray-500">Утас:</span>
                  <span className="text-white ml-2">{app.phone}</span>
                </div>
                <div>
                  <span className="text-gray-500">И-мэйл:</span>
                  <span className="text-white ml-2">{app.email}</span>
                </div>
              </div>

              {/* Admin note */}
              {app.adminNote && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 mb-4">
                  <p className="text-blue-300 text-sm">
                    <span className="font-semibold">Зөвлөхийн тэмдэглэл:</span> {app.adminNote}
                  </p>
                </div>
              )}

              {app.status === "docs_needed" && (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-3 mb-4">
                  <p className="text-orange-300 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Нэмэлт баримт бичиг шаардлагатай байна. Манай зөвлөхтэй холбогдоно уу.
                  </p>
                </div>
              )}

              {/* Progress steps */}
              <div className="flex items-center gap-1 flex-wrap">
                {["pending", "reviewing", "processing", "submitted", "completed"].map((s, i) => {
                  const statuses = ["pending", "reviewing", "processing", "submitted", "completed"];
                  const currentIdx = statuses.indexOf(app.status);
                  const isDone = i <= currentIdx;
                  const cfg = STATUS_CONFIG[s];
                  return (
                    <div key={s} className="flex items-center">
                      <div
                        className={`text-xs px-2 py-1 rounded-lg ${
                          isDone ? `${cfg.bg} ${cfg.color}` : "bg-white/5 text-gray-600"
                        }`}
                      >
                        {cfg?.label}
                      </div>
                      {i < 4 && <ChevronRight className="w-3 h-3 text-gray-600 mx-0.5" />}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Dashboard() {
  const [, navigate] = useLocation();
  const { user, isAuthenticated, loading, logout } = useAuth();

  const { data: applications, isLoading } = trpc.applications.myList.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A1628]">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A1628] text-white px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Нэвтрэх шаардлагатай</h2>
          <p className="text-gray-400 mb-6">Захиалгуудаа харахын тулд нэвтэрнэ үү.</p>
          <a
            href={getLoginUrl()}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Нэвтрэх <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  const stats = {
    total: applications?.length || 0,
    pending: applications?.filter((a) => a.status === "pending").length || 0,
    approved: applications?.filter((a) => a.status === "approved" || a.status === "completed").length || 0,
    processing: applications?.filter((a) => ["reviewing", "processing", "submitted"].includes(a.status)).length || 0,
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-[#0A1628] flex flex-col">
        <Header />

        <div className="flex-1 pt-28 pb-10 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-white">Миний захиалгууд</h1>
                <p className="text-gray-400 text-sm mt-1">
                  Сайн байна уу, <span className="text-blue-400">{user?.name || "хэрэглэгч"}</span>!
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate("/apply")}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" /> Шинэ захиалга
                </button>
                <button
                  onClick={() => logout()}
                  className="flex items-center gap-2 text-gray-400 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2.5 rounded-xl text-sm transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Гарах</span>
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { label: "Нийт захиалга", value: stats.total, color: "text-white", bg: "bg-white/5" },
                { label: "Хүлээгдэж байна", value: stats.pending, color: "text-amber-400", bg: "bg-amber-400/10" },
                { label: "Боловсруулж байна", value: stats.processing, color: "text-blue-400", bg: "bg-blue-400/10" },
                { label: "Зөвшөөрөгдсөн", value: stats.approved, color: "text-green-400", bg: "bg-green-400/10" },
              ].map((s) => (
                <div key={s.label} className={`${s.bg} border border-white/10 rounded-2xl p-4`}>
                  <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-gray-400 text-xs mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Applications List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : applications && applications.length > 0 ? (
              <div className="space-y-3">
                {applications.map((app) => (
                  <ApplicationCard key={app.id} app={app} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-gray-500" />
                </div>
                <h3 className="text-white font-semibold mb-2">Захиалга байхгүй байна</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Визийн захиалга хийж, явцаа энд хянаарай.
                </p>
                <button
                  onClick={() => navigate("/apply")}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  <Plus className="w-4 h-4" /> Захиалга хийх
                </button>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
